import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Trash2, LogOut, Inbox, Megaphone, MessageCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "bg-[#121212] border-white/20 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#ff5a00] focus-visible:border-[#ff5a00]";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("notices");
  const [notices, setNotices] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [reply, setReply] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "info", pinned: false });
  const prevCustomerCount = useRef(null);
  const lastTyping = useRef(0);
  const [chatAlert, setChatAlert] = useState(null);

  const beep = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const chime = (t0) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.type = "triangle";
        o.frequency.setValueAtTime(1318, t0);
        o.frequency.setValueAtTime(1760, t0 + 0.09);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.6, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
        o.start(t0);
        o.stop(t0 + 0.42);
      };
      chime(ctx.currentTime);
      chime(ctx.currentTime + 0.22);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const load = () => {
    api.get("/notices").then((r) => setNotices(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/chats").then((r) => setChats(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  useEffect(() => {
    if (!user) return;
    const poll = async () => {
      api.post("/admin/presence").catch(() => {});
      try {
        const { data } = await api.get("/admin/chats");
        setChats(data);
        setActiveChat((a) => (a ? data.find((c) => c.session_id === a.session_id) || a : a));
        const count = data.reduce((n, c) => n + (c.messages || []).filter((m) => m.sender === "customer").length, 0);
        if (prevCustomerCount.current !== null && count > prevCustomerCount.current) {
          beep();
          const newest = data.reduce((a, b) => (a && a.updated_at > b.updated_at ? a : b), null);
          const lastMsg = newest?.messages?.[newest.messages.length - 1]?.text || "";
          setChatAlert({ name: newest?.name || "Asiakas", text: lastMsg });
        }
        prevCustomerCount.current = count;
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [user, beep]);

  useEffect(() => {
    if (!chatAlert) return;
    const t = setTimeout(() => setChatAlert(null), 15000);
    return () => clearTimeout(t);
  }, [chatAlert]);

  const onReplyChange = (e) => {
    setReply(e.target.value);
    const now = Date.now();
    if (activeChat && now - lastTyping.current > 2000) {
      lastTyping.current = now;
      api.post(`/admin/chats/${activeChat.session_id}/typing`).catch(() => {});
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !activeChat) return;
    try {
      const { data } = await api.post(`/admin/chats/${activeChat.session_id}/reply`, { message: reply });
      const updated = { ...activeChat, messages: [...activeChat.messages, data.message] };
      setActiveChat(updated);
      setChats((cs) => cs.map((c) => (c.session_id === updated.session_id ? updated : c)));
      setReply("");
    } catch { toast.error("Vastaus epäonnistui"); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync");
      if (data.status === "already_running") {
        toast.info("Synkronointi on jo käynnissä…");
      } else {
        toast.info("Synkronointi aloitettu, haetaan autoja Nettiautosta…");
      }
      // Poll status until the background sync finishes (max ~3 min)
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: s } = await api.get("/admin/sync/status");
        if (!s.running) {
          if (s.last_error) {
            toast.error(`Synkronointi epäonnistui: ${s.last_error}`);
          } else if (s.last_result) {
            toast.success(`Synkronoitu: ${s.last_result.synced} autoa, kuvat päivitetty ${s.last_result.details_refreshed}, poistettu ${s.last_result.removed}`);
          } else {
            toast.success("Synkronointi valmis");
          }
          break;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Synkronointi epäonnistui");
    } finally {
      setSyncing(false);
    }
  };

  const addNotice = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/notices", form);
      toast.success("Tiedote lisätty");
      setForm({ title: "", body: "", category: "info", pinned: false });
      load();
    } catch { toast.error("Lisäys epäonnistui"); }
  };

  const delNotice = async (id) => {
    await api.delete(`/admin/notices/${id}`);
    toast.success("Tiedote poistettu");
    load();
  };

  if (loading || !user) return <div className="pt-40 text-center font-mono text-white/40">Ladataan…</div>;

  return (
    <div className="pt-28 pb-24 min-h-screen">
      {chatAlert && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm"
          onClick={() => setChatAlert(null)} data-testid="chat-alert-overlay">
          <div onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#121212] border-2 border-[#ff5a00] shadow-2xl shadow-[#ff5a00]/30 p-10 text-center animate-pulse-once">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#ff5a00] flex items-center justify-center mb-6">
              <MessageCircle size={30} className="text-white" />
            </div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-3">Uusi chat-viesti</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-4">{chatAlert.name}</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8 line-clamp-4">"{chatAlert.text}"</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => { setTab("chats"); setChatAlert(null); }} data-testid="chat-alert-open"
                className="inline-flex items-center justify-center gap-2 bg-[#ff5a00] text-white font-mono text-sm uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-black transition-colors">
                Avaa keskustelu
              </button>
              <button onClick={() => setChatAlert(null)} data-testid="chat-alert-close"
                className="inline-flex items-center justify-center border border-white/25 text-white font-mono text-sm uppercase tracking-widest px-8 py-4 hover:border-white transition-colors">
                Sulje
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-2">Ylläpito</p>
            <h1 className="font-display text-4xl tracking-tight">Hallintapaneeli</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/brandkit")} data-testid="brandkit-link"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-[#ff5a00] transition-colors">
              Brand Kit
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-mono text-xs uppercase tracking-widest px-5 py-3 hover:border-[#ff5a00] transition-colors">
              <LogOut size={15} /> Kirjaudu ulos
            </button>
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-display text-xl">Nettiauto-synkronointi</p>
            <p className="text-white/50 text-sm mt-1">Hakee autot uudelleen ja poistaa myydyt automaattisesti.</p>
          </div>
          <button onClick={sync} disabled={syncing} data-testid="sync-btn"
            className="inline-flex items-center gap-2 bg-[#ff5a00] text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50">
            <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Synkronoidaan…" : "Synkronoi nyt"}
          </button>
        </div>

        <div className="flex gap-2 mb-8">
          {[["notices", "Tiedotteet", Megaphone], ["chats", "Chat", MessageCircle], ["enquiries", "Yhteydenotot", Inbox]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 border transition-colors ${
                tab === k ? "border-[#ff5a00] text-[#ff5a00]" : "border-white/15 text-white/60"}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>

        {tab === "notices" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <form onSubmit={addNotice} className="space-y-4" data-testid="notice-form">
              <h3 className="font-display text-xl">Uusi tiedote</h3>
              <Input required placeholder="Otsikko" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} data-testid="notice-title" />
              <Textarea required placeholder="Sisältö" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={field} data-testid="notice-body" />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className={field} data-testid="notice-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/20 text-white">
                  <SelectItem value="info">Tiedote</SelectItem>
                  <SelectItem value="hours">Aukiolo</SelectItem>
                  <SelectItem value="offer">Tarjous</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/60">
                <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="notice-pinned" />
                Kiinnitä ylös
              </label>
              <button type="submit" data-testid="notice-submit"
                className="bg-[#ff5a00] text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-colors">
                Lisää tiedote
              </button>
            </form>
            <div className="space-y-3">
              <h3 className="font-display text-xl">Julkaistut ({notices.length})</h3>
              {notices.map((n) => (
                <div key={n.id} className="border border-white/10 p-4 flex justify-between gap-4" data-testid={`admin-notice-${n.id}`}>
                  <div>
                    <p className="font-medium">{n.pinned ? "📌 " : ""}{n.title}</p>
                    <p className="text-white/50 text-sm mt-1">{n.body}</p>
                  </div>
                  <button onClick={() => delNotice(n.id)} data-testid={`delete-notice-${n.id}`}
                    className="text-white/40 hover:text-[#ff5a00] transition-colors shrink-0"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "chats" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-chats">
            <div className="space-y-2 lg:col-span-1">
              {chats.length === 0 && <p className="text-white/40 font-mono text-sm">Ei keskusteluja.</p>}
              {chats.map((c) => (
                <button key={c.session_id} onClick={() => setActiveChat(c)} data-testid={`chat-thread-${c.session_id}`}
                  className={`w-full text-left border p-4 transition-colors ${
                    activeChat?.session_id === c.session_id ? "border-[#ff5a00]" : "border-white/10 hover:border-white/30"}`}>
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-white">{c.name}</p>
                    {c.unread && <span className="h-2 w-2 rounded-full bg-[#ff5a00]" />}
                  </div>
                  <p className="text-white/50 text-xs mt-1">{c.contact}</p>
                  {c.car && <p className="text-white/40 text-[11px] mt-1 truncate">{c.car}</p>}
                  <p className="text-white/40 text-xs mt-2 truncate">{c.messages[c.messages.length - 1]?.text}</p>
                </button>
              ))}
            </div>
            <div className="lg:col-span-2 border border-white/10 flex flex-col" style={{ minHeight: "60vh" }}>
              {!activeChat ? (
                <div className="flex-1 flex items-center justify-center text-white/40 font-mono text-sm">Valitse keskustelu</div>
              ) : (
                <>
                  <div className="border-b border-white/10 p-4">
                    <p className="font-display text-lg">{activeChat.name}</p>
                    <p className="text-white/50 text-sm">{activeChat.contact}{activeChat.car ? ` · ${activeChat.car}` : ""}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: "45vh" }}>
                    {activeChat.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.sender === "seller" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-3 py-2 text-sm ${
                          m.sender === "seller" ? "bg-[#ff5a00] text-white" : "bg-[#1a1a1a] text-white border border-white/10"}`}>
                          {m.text}
                        </div>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={sendReply} className="border-t border-white/10 p-3 flex gap-2" data-testid="admin-reply-form">
                    <Input placeholder="Vastaa asiakkaalle…" value={reply} onChange={onReplyChange} className={field} data-testid="admin-reply-input" />
                    <button type="submit" data-testid="admin-reply-send"
                      className="bg-[#ff5a00] text-white px-4 hover:bg-white hover:text-black transition-colors"><Send size={18} /></button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-3">
            {enquiries.length === 0 && <p className="text-white/40 font-mono">Ei yhteydenottoja.</p>}
            {enquiries.map((e) => (
              <div key={e.id} className="border border-white/10 p-5" data-testid={`enquiry-${e.id}`}>
                <div className="flex justify-between items-start">
                  <p className="font-display text-lg">{e.name} <span className="font-mono text-[10px] uppercase tracking-widest text-[#ff5a00] ml-2">{e.type === "tradein" ? "Vaihtoauto" : "Yhteydenotto"}</span></p>
                  <p className="font-mono text-xs text-white/40">{new Date(e.created_at).toLocaleString("fi-FI")}</p>
                </div>
                <p className="text-white/60 text-sm mt-2">{e.email} · {e.phone || "—"}</p>
                {e.car_info && <p className="text-white/60 text-sm mt-1">Auto: {e.car_info} {e.year ? `(${e.year})` : ""} {e.mileage ? `· ${e.mileage} km` : ""}</p>}
                {e.message && <p className="text-white/80 mt-3">{e.message}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
